import {observer} from "mobx-react-lite";
import {HistoryButtons} from "./HistoryButtons";
import {Button, FormControl, Grow, InputLabel, MenuItem, Popper, Select, TextField, Tooltip} from "@mui/material";
import {
    AdminPanelSettings,
    ArrowRightAlt,
    AutoMode,
    CallMerge,
    Check,
    ChevronRight,
    CloudDownload,
    DeleteOutline,
    Description,
    Done,
    Edit,
    LocalHospital,
    Sync,
    UnfoldLess,
    UnfoldMore
} from "@mui/icons-material";
import React, {useState} from "react";
import {useStores} from "../../stores";
import {generatePath, useNavigate, useParams} from "react-router";
import type {DatabagRouteMatch} from "../../routing/catRoutes";
import {routes} from "../../routing/routes";
import {environment} from "../../env";
import {generateGotoTaxonomySuggestor} from "../../routing/routing.utils";
import {analyticsExportEvent} from "../../services/analytics/AnalyticEvents";
import {downloadAsFile} from "../../components/download-data-button/DownloadAsFile";
import type {
    TaxonomyNodeValueMode
} from "../../components/visualization/taxonomy-editor/taxonomy-tree/TaxonomyTreeValueModes.ts";
import {range} from "d3";

type Props = {
    viewer?: boolean
}
export const TaxonomyEditorButtonBar: React.FC<Props> = observer(({viewer}) => {
    const {
        rootAppStore,
        p,
        authStore,
        taxonomyEditorStore,
        taxonomyManagerStore,
        materializedApi,
        bagStore,
        taxonomyHealthCheckStore,
    } = useStores();
    const params = useParams<DatabagRouteMatch>();
    const bagId = bagStore.bagId;
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const blocked = taxonomyManagerStore.taxonomy?.has_taxonomy_health_check;

    if (blocked) {
        return <>
            {p.p.useAiTaxonomyHealthCheck && <Button
                color="primary"
                variant="contained"
                className="toolbar-btn large-icon-btn"
                startIcon={<LocalHospital/>}
                onClick={() => {
                    navigate(generatePath(routes.demo_cleanup_taxonomy_health_check, {
                        id: bagStore.bagId,
                        taxonomyId: p.p.hardcodedOriginalTaxonomyId || -1,
                    }))
                }}>
                Continue Health
            </Button>}
        </>
    }

    if (viewer) return null;

    const handleCloseSettings = () => {
        setAnchorEl(null);
    };

    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false)
    const [hasDownloadedExcel, setHasDownloadedExcel] = useState(false)

    const allButtonsDisabled = !taxonomyManagerStore.canEditTaxonomy;

    // FIXME: We have a problem in the back button and the router.
    const showAiTaxonomySuggestionButton = p.p.useAiTaxonomySuggestion
        && environment.package === 'sales_demo';

    const showDemoTaxonomySuggestion = p.p.demoFakeTaxonomySuggestion && (
        environment.package === 'sales_demo' && rootAppStore.app.appSection === 'cleanup'
    );

    const taxonomyHealthCheckUrl =
        (environment.package === 'sales_demo') ? routes.demo_cleanup_taxonomy_health_check : '';

    return <div id="taxonomy-editor-button-toolbar">

        {/*Mithra Staff options*/}
        {authStore.isMithraStaffGroup && <>
            {taxonomyEditorStore.isEditMode
                ? <TextField
                    className="taxonomy-title-edit-field"
                    label={<><AdminPanelSettings/> Taxonomy name</>}
                    // variant="standard"
                    value={taxonomyManagerStore.taxonomy?.name || ''}
                    onChange={event => {
                        taxonomyManagerStore.setTaxonomyName(event.target.value)
                    }}
                    disabled={taxonomyManagerStore._updateTaxonomyRequest.busy}
                    onBlur={() => taxonomyManagerStore.storeTaxonomyName()}
                />
                : <TextField
                    label={<><AdminPanelSettings/> Taxonomy name</>}
                    disabled
                    defaultValue={taxonomyManagerStore.taxonomy?.name}
                />
            }

            <FormControl>
                <InputLabel id="assigned-taxonomy-select-label">
                    <AdminPanelSettings/>
                    Linked taxonomy
                </InputLabel>
                <Select
                    label="Linked taxonomy"
                    labelId="assigned-taxonomy-select-label"
                    variant="standard"
                    value={taxonomyManagerStore.taxonomy?.id || ''}
                    onChange={(e) => {
                        if (Number(e.target.value) === 0) return;
                        taxonomyManagerStore.setTaxonomyInDatabag(Number(e.target.value))
                    }}>
                    {taxonomyManagerStore.allTaxonomyList?.map(m =>
                        <MenuItem key={m.id} value={m.id}>
                            [{m.id}] {m.name}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
            <FormControl style={{minWidth: '5em'}}>
                <InputLabel id="taxonomy-size-select-label">
                    <AdminPanelSettings/>
                    Size
                </InputLabel>
                <Select
                    label="Size"
                    labelId="taxonomy-size-select-label"
                    variant="standard"
                    value={taxonomyManagerStore.taxonomy?.size || 2}
                    onChange={(e) => {
                        if (Number(e.target.value) === 0) return;
                        taxonomyManagerStore.updateTaxonomySize(Number(e.target.value))
                    }}>
                    {range(8).map(i =>
                        <MenuItem key={i} value={i + 1}>
                            {i + 1}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
        </>}

        {!taxonomyEditorStore.isEditMode && <Button
            startIcon={taxonomyEditorStore.isFullViewMode ? <UnfoldLess/> : <UnfoldMore/>}
            onClick={() => taxonomyEditorStore.toggleViewMode()}>
            {taxonomyEditorStore.isFullViewMode ? 'Fit to screen' : 'Expand'}
        </Button>
        }
        {taxonomyEditorStore.isEditMode ? <HistoryButtons/> : undefined}
        {p.p.useAiTaxonomyHealthCheck && <Button
            color="primary"
            variant="contained"
            className="toolbar-btn large-icon-btn"
            startIcon={<LocalHospital/>}
            disabled={environment.isDemo
                ? (!taxonomyHealthCheckStore.hasHealthCheck) // We disable this in the demo to show in which databag we have a health check and not...
                : taxonomyEditorStore.disableHealth
            }
            onClick={() => {
                if (environment.isDemo) {
                    // Demo variant:
                    navigate(generatePath(taxonomyHealthCheckUrl, {
                        id: bagId,
                        taxonomyId: p.p.hardcodedOriginalTaxonomyId || -1,
                    }))
                } else {
                    // Real variant:
                    const taxonomyId = taxonomyManagerStore.taxonomy?.id;
                    if (!taxonomyId) return;
                    taxonomyEditorStore.setHealthButtonText("Health is being created...")
                    taxonomyEditorStore.setDisableHealth(true)
                    taxonomyHealthCheckStore.createNewTaxonomyHealthCheck(taxonomyId).then(() => {
                        // We have successfully created a new taxonomy health check
                        taxonomyManagerStore.requestTaxonomy(taxonomyId)
                        taxonomyHealthCheckStore.getTaxonomyHealthCheck(taxonomyId).then(() => {
                            taxonomyManagerStore.overwriteTaxonomyHealthCheckResult(true)
                            taxonomyEditorStore.setHealthButtonText("Health")
                            taxonomyEditorStore.setDisableHealth(false)
                            navigate(generatePath(routes.demo_cleanup_taxonomy_health_check, {id: bagId, taxonomyId}))
                        }).catch(() => {
                            taxonomyEditorStore.setHealthButtonText("Health")
                            taxonomyEditorStore.setDisableHealth(false)
                        });
                    }).catch(() => {
                        taxonomyEditorStore.setHealthButtonText("Health")
                        taxonomyEditorStore.setDisableHealth(false)
                    });
                }
            }}>
            {taxonomyEditorStore.healthButtonText}
        </Button>}
        {showAiTaxonomySuggestionButton && <Button
            color="primary"
            variant="contained"
            className="toolbar-btn large-icon-btn"
            onClick={() => {
                navigate(generateGotoTaxonomySuggestor(bagId, p.p.hardcodedOriginalTaxonomyId || -1))
            }}>
            AI suggestions
        </Button>}
        {showDemoTaxonomySuggestion && <Button
            color="primary"
            variant="outlined"
            className="toolbar-btn large-icon-btn"
            startIcon={<AutoMode/>}
            onClick={() => {
                // Demo variant
                navigate(generatePath(routes.demo_cleanup_taxonomy_suggestion_v1, params))
            }}>
            Generate Suggestion
        </Button>}
        {/*<Button*/}
        {/*    color="primary"*/}
        {/*    variant="outlined"*/}
        {/*    className="toolbar-btn large-icon-btn"*/}
        {/*    startIcon={<ChevronLeft/>}*/}
        {/*    disabled={!taxonomyEditorStore.canGoUp}*/}
        {/*    onClick={() => taxonomyEditorStore.goUpLevel()}>*/}
        {/*    {taxonomyEditorStore.goBackButtonTxt}*/}
        {/*</Button>*/}
        <Button
            className="toolbar-btn large-icon-btn btn-red"
            color="primary"
            variant="outlined"
            startIcon={<DeleteOutline/>}
            disabled={allButtonsDisabled || taxonomyEditorStore.selection.length === 0}
            style={taxonomyEditorStore.isEditMode ? {} : {display: 'none'}}
            onClick={() => taxonomyEditorStore.deleteSelection()}>
            Delete
        </Button>
        <Button
            className="toolbar-btn large-icon-btn"
            color="primary"
            variant="outlined"
            startIcon={<CallMerge style={{transform: 'rotate(90deg)'}}/>}
            disabled={allButtonsDisabled || taxonomyEditorStore.selection.length !== 2}
            style={taxonomyEditorStore.isEditMode ? {} : {display: 'none'}}
            onClick={() => taxonomyEditorStore.setMergeCategoryMode(true)}>
            Merge
        </Button>
        <Button
            className="toolbar-btn large-icon-btn"
            color="primary"
            variant="outlined"
            startIcon={<ArrowRightAlt/>}
            disabled={allButtonsDisabled || taxonomyEditorStore.selection.length === 0}
            style={taxonomyEditorStore.isEditMode ? {} : {display: 'none'}}
            onClick={() => taxonomyEditorStore.setMoveCategoryMode(true)}>
            Move
        </Button>
        <Button
            className="toolbar-btn large-icon-btn"
            color="primary"
            variant="outlined"
            startIcon={<Description/>}
            disabled={allButtonsDisabled || taxonomyEditorStore.selection.length !== 1}
            style={taxonomyEditorStore.isEditMode ? {} : {display: 'none'}}
            onClick={() => taxonomyEditorStore.setUpdateCategoryMode(true)}>
            Rename
        </Button>
        <Tooltip
            title={!authStore.hasEditPermission ? 'Your account is view-only, you cannot edit the taxonomy' : null}>
            <span>
                <Button
                    className="toolbar-btn large-icon-btn"
                    color="primary"
                    variant="outlined"
                    startIcon={<Edit/>}
                    disabled={allButtonsDisabled || !authStore.hasEditPermission}
                    onClick={() => taxonomyEditorStore.toggleEditMode()}>
                    {taxonomyEditorStore.isEditMode ? 'Finish editing' : 'Edit'}
                </Button>
            </span>
        </Tooltip>

        <Popper
            id="taxonomy-editor-button-settings-popper"
            open={open}
            anchorEl={anchorEl}
            placement="bottom"
            transition
            disablePortal
        >
            {({TransitionProps, placement}) =>
                // Collapse is not possible: https://github.com/mui/material-ui/issues/11337
                <Grow
                    {...TransitionProps}
                    style={{
                        transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                >
                    <div className="overlay-container">
                        <Button
                            disabled
                            className="toolbar-btn large-icon-btn"
                            color="primary"
                            variant="outlined"
                            startIcon={<Description/>}
                            onClick={handleCloseSettings}>
                            <div className="btn-text">
                                View<br/> change log
                            </div>
                        </Button>
                        <Button
                            disabled
                            className="toolbar-btn large-icon-btn"
                            color="primary"
                            variant="outlined"
                            startIcon={<Sync/>}
                            onClick={handleCloseSettings}>
                            <div className="btn-text">
                                Calculate<br/> new amounts
                            </div>
                        </Button>
                        <Button
                            disabled
                            className="toolbar-btn large-icon-btn"
                            color="primary"
                            variant="outlined"
                            startIcon={<Check/>}
                            onClick={handleCloseSettings}>
                            <div className="btn-text">
                                Submit for approval
                            </div>
                        </Button>
                    </div>
                </Grow>
            }
        </Popper>
        <Button
            className="toolbar-btn large-icon-btn"
            color="primary"
            variant="outlined"
            startIcon={isDownloadingExcel ? undefined : (hasDownloadedExcel ? <Done/> :
                <CloudDownload/>)}
            endIcon={isDownloadingExcel ? undefined : (hasDownloadedExcel ? undefined : undefined)}
            disabled={allButtonsDisabled || isDownloadingExcel}
            style={taxonomyEditorStore.isEditMode ? {display: 'none'} : {minWidth: '10em'}}
            onClick={() => {
                const taxonomyId = taxonomyManagerStore.taxonomy?.id;
                if (taxonomyId) {
                    setIsDownloadingExcel(true)
                    analyticsExportEvent({
                        export_type: 'taxonomy',
                        object_key: String(taxonomyId),
                        user: authStore.email
                    })
                    const filename = 'Taxonomy.xlsx';
                    materializedApi.downloadTaxonomyExcel(taxonomyId, filename)
                        .then(resp => {
                            downloadAsFile(resp, filename);
                            setHasDownloadedExcel(true)
                        })
                        .finally(() => {
                            setIsDownloadingExcel(false)
                        })
                }
            }}>
            {isDownloadingExcel ? 'Downloading ...' : 'Download'}
        </Button>
        {taxonomyEditorStore.allTaxonomyValueModes.length > 1 &&
            <Select
                className="toolbar-select-btn"
                variant="standard"
                value={taxonomyEditorStore.valueMode.key}
                disabled={taxonomyEditorStore.isEditMode}
                onChange={e => taxonomyEditorStore.setValueMode(e.target.value as TaxonomyNodeValueMode['key'])}>
                {taxonomyEditorStore.allTaxonomyValueModes.map(m =>
                    <MenuItem key={m.key} value={m.key}>
                        {m.label}
                    </MenuItem>
                )}
            </Select>
        }
        {!p.p.hideApprovalButtons && authStore.hasEditPermission &&
            <Tooltip title={taxonomyManagerStore.canGotToApprovalErrorMessage}>
                <span>
                    <Button
                        className="toolbar-btn large-icon-btn"
                        color="primary"
                        variant="outlined"
                        disabled={Boolean(taxonomyManagerStore.canGotToApprovalErrorMessage)}
                        style={taxonomyEditorStore.isEditMode ? {display: 'none'} : {}}
                        onClick={() => taxonomyEditorStore.setPage('approval')}
                        endIcon={<ChevronRight/>}
                    >
                        Approval
                    </Button>
                </span>
            </Tooltip>
        }
    </div>
})
